import { toast } from 'react-toastify'
import * as yup from 'yup'
import yupLocaleDe from 'yup-locale-de'

import { AuthActions, LogoutReason } from '../actions/authActions'
import { CompanyActions } from '../actions/companyActions'
import { MenuActions } from '../actions/menuActions'
import { OfflineActions } from '../actions/offlineActions'
import { TeamActions } from '../actions/teamActions'
import { UserActions } from '../actions/userActions'
import api from '../api'
import ICorporation from '../api/types/corporation'
import ITeam from '../api/types/team'
import IUser from '../api/types/user'
import i18n from '../i18n'
import { AlphaprocessDatabase } from '../offlineMode/offlineDb'
import { logoutWebPush } from '../push/webPush'
import DateUtil from '../util/DateUtil'

export interface ICoreState {
  currentLanguage: string
  company: ICorporation | null
  isMenuOpen: boolean
  jwt: string | null
  notificationToken: string | null
  team: ITeam | null
  user: IUser | null
  isOffline: boolean
  offlineManual?: boolean
  browserNotificationClosedDate: number | null
  startWithTemplateOpen: boolean
  logoutReason: LogoutReason | null
  // shared-link visitor (no user): the company's design switch, see useNewDesign
  visitorNewDesign?: boolean
}

const initialState: ICoreState = {
  company: null,
  isMenuOpen: false,
  jwt: null,
  notificationToken: null,
  team: null,
  user: null,
  isOffline: false,
  offlineManual: false,
  currentLanguage: navigator.language.substr(0, 2),
  browserNotificationClosedDate: null,
  startWithTemplateOpen: true,
  logoutReason: null
}

export type CoreAction = AuthActions | CompanyActions |
MenuActions | TeamActions | UserActions | OfflineActions

export default function coreReducer(
  state: ICoreState = initialState,
  action: CoreAction
): ICoreState {
  switch (action.type) {
    case 'AUTH_LOGIN':
      api.addToken(action.jwt)
      localStorage.setItem('jwt', action.jwt)
      return {
        ...state,
        jwt: action.jwt,
        user: action.user,
        logoutReason: null
      }
    case 'AUTH_LOGOUT':
      const db = new AlphaprocessDatabase()
      db.tables.forEach(async (t) => await t.clear())
      localStorage.removeItem('jwt')
      logoutWebPush()
      api.removeNotification().then(() => {
        api.logout().finally(() => {
          api.removeToken()
        })
      })
      // remove wiki cookie
      document.cookie = 'accessToken=; expires = Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=.alphaprocess.io;'
      try {
        if ('$chatwoot' in window) {
          (window as any).$chatwoot.reset()
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('error resetting chatwoot', error)
      }
      return {
        ...initialState,
        logoutReason: action.reason ?? null
      }
    case 'TEAM_ADD':
      return {
        ...state,
        team: action.team
      }
    case 'COMPANY_ADD':
      return {
        ...state,
        company: action.company
      }
    case 'MENU_TOGGLE':
      return {
        ...state,
        isMenuOpen: !state.isMenuOpen
      }

    case 'USER_UPDATE':
      return {
        ...state,
        user: action.user
      }
    case 'USER_SET_NOTIFICATION_TOKEN':
      return {
        ...state,
        notificationToken: action.token
      }
    case 'CORE_SET_OFFLINE_MODE':
      if (!state.isOffline && action.isOffline) {
        toast(i18n.t('offlineMode.enteringOfflineMode'), { type: 'default' })
      }
      return {
        ...state,
        isOffline: action.isOffline,
        // a failed reconnect attempt must not turn a manual offline mode into an automatic one
        offlineManual: action.isOffline && (action.manual || (state.isOffline && !!state.offlineManual))
      }
    case 'CORE_SET_VISITOR_DESIGN':
      return {
        ...state,
        visitorNewDesign: action.newDesign
      }
    case 'SET_LANGUAGE':
      DateUtil.setLocale(action.lang)
      yup.setLocale(yupLocaleDe)
      i18n.changeLanguage(action.lang)
      return {
        ...state,
        currentLanguage: action.lang
      }
    case 'SET_BROWSER_NOTIFICATION_CLOSE_DATE':
      return {
        ...state,
        browserNotificationClosedDate: action.closedDate
      }
    case 'TOGGLE_START_WITH_TEMPLATE':
      return {
        ...state,
        startWithTemplateOpen: !state.startWithTemplateOpen
      }
    default:
      return state
  }
}
